import React, { useState, useEffect, useMemo } from 'react';
import { Box, Grid, Card, CardContent, CardMedia, Typography, TextField, Button, MenuItem, Select, FormControl, InputLabel, IconButton, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Chip, ToggleButtonGroup, ToggleButton, Switch, Checkbox, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CircleIcon from '@mui/icons-material/Circle';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import CloseIcon from '@mui/icons-material/Close';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import LockIcon from '@mui/icons-material/Lock';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { printElement, BluetoothPrinterManager, printOrderReceipt, printKOTHTML } from '../utils/printHelper';
import BottomNavigationBar from '../components/BottomNavigationBar';

export default function POSTerminalView() {
    const { 
        tables, 
        sections,
        orders, 
        selectedTableId, 
        selectTable, 
        activeCart, 
        addToCart, 
        removeFromCart, 
        updateRemarks, 
        sendKOT, 
        settleBill,
        menuItems,
        categories,
        subscribedModules,
        lockedActions,
        setSidebarCollapsed,
        setSidebarMobileOpen,
        cancelOrder,
        setActiveView,
        pinnedItems = [],
        togglePinItem,
        showMenuImages,
        posCardLayout = 'compact2',
        currentUser,
        refreshMenuCatalog,
        showToast,
        branchSettings,
        getActiveBranchId,
        generateUniqueOrderId,
        generateUniqueKOTId
    } = useApp();

    const currentStaffName = currentUser?.name || currentUser?.username || 'Counter Staff';

    const { t, currentLanguage } = useLanguage();

    // Determine if the current session originated from a specific physical table on Floor Plan
    const isPhysicalTable = Boolean(selectedTableId && selectedTableId !== 'counter' && !String(selectedTableId).startsWith('ORD-'));
    const selectedPhysicalTable = isPhysicalTable ? (tables || []).find(t => String(t.id) === String(selectedTableId) || String(t.number) === String(selectedTableId)) : null;

    const [orderSource, setOrderSource] = useState(() => {
        if (isPhysicalTable) return 'dine_in';
        return 'takeaway';
    });

    // Synchronize orderSource strictly based on whether entering from a physical table vs direct POS
    useEffect(() => {
        if (isPhysicalTable) {
            setOrderSource('dine_in');
        } else if (orderSource === 'dine_in') {
            setOrderSource('takeaway');
        }
    }, [selectedTableId, isPhysicalTable]);

    // Auto-select Table or Counter Parcel if user taps dishes without selecting a table first
    const handleAddToCartAuto = (item) => {
        if (!selectedTableId) {
            selectTable('counter');
        }
        addToCart(item);
    };

    const [aggregatorOrderId, setAggregatorOrderId] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    // Performance optimization for low-RAM tablets: Default to 1st category instead of 'all' to prevent rendering 100+ items at once
    const [selectedCategory, setSelectedCategory] = useState(() => {
        if (categories && Array.isArray(categories) && categories.length > 0) {
            const sorted = [...categories].sort((a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0));
            return sorted[0].id;
        }
        return 'all';
    });

    // Auto-select first category if currently on 'all' and categories load
    useEffect(() => {
        if (selectedCategory === 'all' && !searchQuery && categories && Array.isArray(categories) && categories.length > 0) {
            const sorted = [...categories].sort((a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0));
            setSelectedCategory(sorted[0].id);
        }
    }, [categories]);
    const [discount, setDiscount] = useState(0);
    const [discountReason, setDiscountReason] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [splitCashAmount, setSplitCashAmount] = useState(0);
    const [splitUpiAmount, setSplitUpiAmount] = useState(0);
    const [openCheckoutDialog, setOpenCheckoutDialog] = useState(false);
    const [openCancelDialog, setOpenCancelDialog] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [openReceipt, setOpenReceipt] = useState(false);
    const [receiptPreviewImage, setReceiptPreviewImage] = useState(null);
    const [settledOrderDetails, setSettledOrderDetails] = useState(null);
    const [openKOTPreview, setOpenKOTPreview] = useState(false);
    const [kotDetails, setKotDetails] = useState(null);
    const [serviceChargeEnabled, setServiceChargeEnabled] = useState(false);
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerConsent, setCustomerConsent] = useState(false);
    const [btDeviceName, setBtDeviceName] = useState(() => localStorage.getItem('dinexa_printer_device_name') || '');

    // Active order linked to current table or direct parcel order ID (ignoring cancelled or served orders)
    const directOrder = (selectedTableId && String(selectedTableId).startsWith('ORD-')) ? (orders || []).find(o => String(o.id) === String(selectedTableId) && !['cancelled', 'served'].includes(String(o.status).toLowerCase())) : null;
    const table = (tables || []).find(t => String(t.id) === String(selectedTableId) || String(t.number) === String(selectedTableId));
    const activeOrder = directOrder || (table && table.activeOrderId ? (orders || []).find(o => String(o.id) === String(table.activeOrderId) && !['cancelled', 'served'].includes(String(o.status).toLowerCase())) : null);

    // Memoized category map for 0ms lookup
    const categoryMap = useMemo(() => {
        const map = new Map();
        if (categories && Array.isArray(categories)) {
            categories.forEach(c => {
                const name = currentLanguage === 'mr' ? (c.name_mr || c.name) : c.name;
                map.set(String(c.id), name);
                map.set(String(c.name).toLowerCase(), name);
            });
        }
        return map;
    }, [categories, currentLanguage]);

    const getCategoryName = (item) => {
        if (!item) return 'General';
        const key = String(item.category_id || item.category || '');
        if (categoryMap.has(key)) return categoryMap.get(key);
        return item.category_name || item.category || 'General';
    };

    const activeBranchId = useMemo(() => {
        let storedUserBranch = null;
        try {
            const u = sessionStorage.getItem('react_pos_current_user');
            if (u) storedUserBranch = JSON.parse(u)?.branch_id;
        } catch(e) {}
        return currentUser?.branch_id || storedUserBranch || localStorage.getItem('dinexa_active_branch_id') || 1;
    }, [currentUser]);

    const activeCategories = useMemo(() => {
        if (!categories || !Array.isArray(categories)) return [];
        const filtered = categories.filter(c => !c.branch_id || String(c.branch_id) === String(activeBranchId));
        return filtered.length > 0 ? filtered : categories;
    }, [categories, activeBranchId]);

    // Auto-fetch menu catalog if categories or menu are empty
    useEffect(() => {
        if (typeof refreshMenuCatalog === 'function' && (!categories || categories.length === 0 || !menuItems || menuItems.length === 0)) {
            refreshMenuCatalog().catch(err => console.warn("Auto loading menu:", err));
        }
    }, [categories?.length, menuItems?.length, refreshMenuCatalog]);

    // Filter & sort menu items (Memoized for 0ms render response)
    const filteredMenu = useMemo(() => {
        if (!menuItems || !Array.isArray(menuItems)) return [];
        const query = searchQuery.trim().toLowerCase();
        
        const catObj = activeCategories && Array.isArray(activeCategories) 
            ? activeCategories.find(c => String(c.id) === String(selectedCategory) || (c.name || '').toString().toLowerCase() === (selectedCategory || '').toString().toLowerCase()) 
            : null;
        const targetCatName = catObj ? (catObj.name || '').toString().toLowerCase() : '';

        const branchMenuItems = menuItems.filter(item => !item.branch_id || String(item.branch_id) === String(activeBranchId));
        const finalMenuItems = branchMenuItems.length > 0 ? branchMenuItems : menuItems;

        return finalMenuItems
            .filter(item => {
                const matchesSearch = !query || 
                    (item.name || '').toLowerCase().includes(query) || 
                    (item.nameMr || item.name_mr || '').toLowerCase().includes(query) || 
                    (item.nameHi || item.name_hi || '').toLowerCase().includes(query);
                
                const itemCatName = (item.category || item.category_name || '').toString().toLowerCase();

                const matchesCategory = !!query || selectedCategory === 'all' || 
                    (selectedCategory === 'pinned' && pinnedItems.includes(item.id)) ||
                    String(item.category_id) === String(selectedCategory) || 
                    String(item.category) === String(selectedCategory) ||
                    (targetCatName !== '' && itemCatName === targetCatName) ||
                    (catObj && String(catObj.id) === String(item.category_id));
                
                // If 'pinned' category is selected but we are not searching, only show pinned items
                if (selectedCategory === 'pinned' && !query) {
                    return matchesSearch && pinnedItems.includes(item.id);
                }

                return matchesSearch && matchesCategory;
            })
            .sort((a, b) => {
                const aPinned = pinnedItems.includes(a.id);
                const bPinned = pinnedItems.includes(b.id);
                if (aPinned && !bPinned) return -1;
                if (!aPinned && bPinned) return 1;
                
                const orderA = parseInt(a.display_order !== undefined ? a.display_order : a.displayOrder) || 0;
                const orderB = parseInt(b.display_order !== undefined ? b.display_order : b.displayOrder) || 0;
                if (orderA !== orderB) return orderA - orderB;
                return parseInt(a.id) - parseInt(b.id);
            });
    }, [menuItems, searchQuery, selectedCategory, activeCategories, pinnedItems, activeBranchId]);

    const getComplianceDetails = (item) => {
        if (!item) return { calories: '0 kcal', allergens: 'None' };
        if (item.category === 'starters') {
            return { calories: '180 kcal', allergens: 'Gluten, Soy' };
        }
        if (item.category === 'maincourse') {
            return { calories: '420 kcal', allergens: 'Dairy, Nuts' };
        }
        if (item.category === 'breads') {
            return { calories: '125 kcal', allergens: 'Gluten, Yeast' };
        }
        if (item.category === 'rice') {
            return { calories: '210 kcal', allergens: 'None' };
        }
        if (item.category === 'beverages') {
            return { calories: '90 kcal', allergens: 'Dairy, Sugar' };
        }
        if (item.category === 'beverages') {
            return { calories: '90 kcal', allergens: 'Caffeine (optional)' };
        }
        return { calories: '150 kcal', allergens: 'Dairy' };
    };

    const checkIsVeg = (item) => {
        if (!item) return true;

        // 1. Explicit veg boolean / integer / string flags
        if (item.veg === true || item.veg === 1 || item.veg === '1' || item.veg === 'true') return true;
        if (item.is_veg === true || item.is_veg === 1 || item.is_veg === '1' || item.is_veg === 'true') return true;

        // 2. Resolve Category Name from categories array
        let catName = (item.category || item.category_name || '').toString();
        if (categories && Array.isArray(categories)) {
            const catObj = categories.find(c => c.id == item.category_id || c.id == item.category);
            if (catObj) {
                catName = (catObj.name || catObj.name_mr || '').toString();
            }
        }
        const catLower = catName.toLowerCase();

        // 3. If Category name is Veg (and not Non-Veg), it IS Veg 100%
        if ((catLower.includes('veg') || catLower.includes('वेज')) && !catLower.includes('non') && !catLower.includes('नॉन')) return true;
        if (catLower.includes('non') || catLower.includes('नॉन')) return false;

        // 4. Check dish name for Non-Veg keywords (English & Marathi)
        const nameStr = ((item.name || '') + ' ' + (item.nameMr || '') + ' ' + (item.name_mr || '')).toLowerCase();
        if (nameStr.includes('chicken') || nameStr.includes('mutton') || nameStr.includes('egg') || nameStr.includes('fish') || nameStr.includes('prawn') || nameStr.includes('keema') || nameStr.includes('चिकन') || nameStr.includes('मटण') || nameStr.includes('अंडे') || nameStr.includes('मासे')) {
            return false;
        }

        // 5. If veg flag is 0/false, but name is a known veg dish in English or Marathi
        if (item.veg === false || item.veg === 0 || item.veg === '0' || item.veg === 'false' || item.is_veg === 0 || item.is_veg === '0') {
            if (nameStr.includes('papad') || nameStr.includes('पापड') || nameStr.includes('paneer') || nameStr.includes('पनीर') || nameStr.includes('dal') || nameStr.includes('डाळ') || nameStr.includes('roti') || nameStr.includes('रोटी') || nameStr.includes('naan') || nameStr.includes('नान') || nameStr.includes('rice') || nameStr.includes('राईस') || nameStr.includes('सूप') || nameStr.includes('ताक') || nameStr.includes('सोडा') || nameStr.includes('सॅलड') || nameStr.includes('भाजी') || nameStr.includes('मसाला')) {
                return true;
            }
            return false;
        }

        return true;
    };

    const getDisplayName = (item) => {
        if (!item) return '';
        if (currentLanguage === 'mr') return item.name_mr || item.nameMr || item.name || '';
        if (currentLanguage === 'hi') return item.name_hi || item.nameHi || item.name || '';
        return item.name || item.name_mr || item.nameMr || '';
    };

    // Recalculate bill summary dynamically based on current cart
    const enableGst = branchSettings 
        ? !!branchSettings.gst_enabled 
        : (localStorage.getItem('dinexa_gst_enabled') === 'true' || localStorage.getItem('dinexa_gst_enabled') === '1');
    const subtotal = activeCart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const tax = enableGst ? (subtotal * 0.05) : 0;
    const serviceCharge = serviceChargeEnabled ? subtotal * 0.05 : 0;
    const finalTotal = Math.max(0, subtotal + tax + serviceCharge - discount);

    useEffect(() => {
        setSplitCashAmount(Math.round(finalTotal));
        setSplitUpiAmount(0);
    }, [finalTotal]);

    useEffect(() => {
        if (setSidebarCollapsed) {
            setSidebarCollapsed(true);
        }
        return () => {
            if (setSidebarCollapsed) setSidebarCollapsed(false);
        }
    }, [setSidebarCollapsed]);

    // Automatically switch orderSource when selectedTableId changes
    useEffect(() => {
        if (directOrder) {
            setOrderSource(directOrder.order_source || directOrder.orderSource || 'takeaway');
        } else if (selectedTableId && selectedTableId !== 'counter' && !String(selectedTableId).startsWith('ORD-')) {
            setOrderSource('dine_in');
        } else if (selectedTableId === 'counter') {
            setOrderSource(prev => (prev === 'dine_in' ? 'takeaway' : prev));
        }
    }, [selectedTableId, directOrder]);

    // Sync payment method when order source is changed to aggregator
    useEffect(() => {
        if (orderSource === 'zomato') {
            setPaymentMethod('zomato');
        } else if (orderSource === 'swiggy') {
            setPaymentMethod('swiggy');
        } else if (['zomato', 'swiggy'].includes(paymentMethod)) {
            setPaymentMethod('cash'); // Revert back to cash if switching away from aggregator
        }
    }, [orderSource]);

    const handleCancelConfirm = () => {
        if (cancelReason.trim() === '') return;
        cancelOrder(cancelReason);
        setOpenCancelDialog(false);
        setCancelReason('');
    };

    const handleSendKOTDirect = () => {
        if (activeCart.length === 0) return;
        if (orderSource === 'dine_in' && (!selectedTableId || selectedTableId === 'counter')) {
            showToast(currentLanguage === 'mr' ? '⚠️ कृपया KOT पाठवण्यापूर्वी टेबल निवडा!' : '⚠️ Please select a dining table before sending KOT!');
            return;
        }
        sendKOT(currentStaffName, orderSource, aggregatorOrderId);
    };

    const handleKOTClick = async () => {
        if (activeCart.length === 0) return;
        if (orderSource === 'dine_in' && (!selectedTableId || selectedTableId === 'counter')) {
            showToast(currentLanguage === 'mr' ? '⚠️ कृपया KOT पाठवण्यापूर्वी टेबल निवडा!' : '⚠️ Please select a dining table before sending KOT!');
            return;
        }
        const activeBranchId = (typeof getActiveBranchId === 'function' ? getActiveBranchId() : null) || currentUser?.branch_id || 1;
        const newKot = {
            id: typeof generateUniqueKOTId === 'function' ? generateUniqueKOTId(activeBranchId) : `KOT-B${activeBranchId}-${Math.floor(100 + Math.random() * 900)}`,
            tableNumber: table ? table.number : (selectedTableId === 'counter' ? 'Counter' : 'Parcel'),
            waiterName: activeOrder ? (activeOrder.waiterName || currentStaffName) : currentStaffName,
            timestamp: new Date().toISOString(),
            items: activeCart.map(cartItem => {
                const menuItem = menuItems.find(m => m.id === cartItem.id);
                return {
                    name: getDisplayName(menuItem),
                    quantity: cartItem.quantity,
                    remarks: cartItem.remarks
                };
            })
        };
        setKotDetails(newKot);
        sendKOT(currentStaffName, orderSource, aggregatorOrderId);

        const connectionType = localStorage.getItem('dinexa_printer_connection_type') || 'system';

        if (connectionType !== 'system') {
            try {
                await BluetoothPrinterManager.printKOTCanvas(newKot, currentLanguage);
            } catch (e) {
                console.warn("KOT direct print failed:", e);
                showToast("⚠️ ब्लूटूथ प्रिंटर उपलब्ध नाही, प्रिव्ह्यू दाखवत आहे");
                setOpenKOTPreview(true);
            }
        } else {
            setOpenKOTPreview(true);
        }
    };

    const handleSettleClick = async () => {
        if (activeCart.length === 0 && !activeOrder) return;
        
        const pMethod = paymentMethod === 'split' 
            ? `Split (Cash: ₹${splitCashAmount}, UPI: ₹${splitUpiAmount})` 
            : paymentMethod;

        const effectiveItems = (activeCart && activeCart.length > 0)
            ? activeCart.map(cartItem => {
                const menuItem = (menuItems || []).find(m => m.id === cartItem.id);
                return {
                    name: getDisplayName(menuItem) || cartItem.name || 'Item',
                    quantity: cartItem.quantity,
                    price: cartItem.price
                };
            })
            : (activeOrder?.items || []).map(item => {
                const menuItem = (menuItems || []).find(m => m.id === (item.menu_item_id || item.id));
                return {
                    name: getDisplayName(menuItem) || item.name || item.name_mr || 'Item',
                    quantity: item.quantity,
                    price: item.price
                };
            });

        const activeBranchId = (typeof getActiveBranchId === 'function' ? getActiveBranchId() : null) || currentUser?.branch_id || 1;
        // Save order details to preview on the receipt print popup
        const mockSettleOrder = {
            id: activeOrder ? activeOrder.id : (typeof generateUniqueOrderId === 'function' ? generateUniqueOrderId(activeBranchId) : `ORD-B${activeBranchId}-${Date.now().toString().slice(-6)}`),
            tableNumber: table ? table.number : 'Unknown',
            waiterName: activeOrder ? (activeOrder.waiterName || currentStaffName) : currentStaffName,
            timestamp: new Date().toISOString(),
            type: activeOrder ? activeOrder.type : 'Dine-In',
            paymentMethod: pMethod,
            items: effectiveItems,
            subtotal,
            tax,
            serviceCharge,
            discount: parseFloat(discount) || 0,
            total: finalTotal,
            customerPhone,
            customerConsent
        };

        setSettledOrderDetails(mockSettleOrder);

        const connectionType = localStorage.getItem('dinexa_printer_connection_type') || 'system';

        try {
            if (connectionType !== 'system') {
                try {
                    await BluetoothPrinterManager.printReceiptCanvas(mockSettleOrder, currentLanguage);
                    confirmReceiptSettle();
                } catch (e) {
                    console.warn("Receipt direct print failed, opening preview:", e);
                    const preview = await BluetoothPrinterManager.generateReceiptDataURL(mockSettleOrder, currentLanguage);
                    setReceiptPreviewImage(preview);
                    setOpenReceipt(true);
                }
            } else {
                const preview = await BluetoothPrinterManager.generateReceiptDataURL(mockSettleOrder, currentLanguage);
                setReceiptPreviewImage(preview);
                setOpenReceipt(true);
            }
        } catch (err) {
            console.error("Print preview failed, settling order directly:", err);
            confirmReceiptSettle();
        }
    };

    const confirmReceiptSettle = () => {
        try {
            const pMethod = paymentMethod === 'split' 
                ? `Split (Cash: ₹${splitCashAmount}, UPI: ₹${splitUpiAmount})` 
                : paymentMethod;
            settleBill(parseFloat(discount) || 0, serviceCharge, pMethod, discountReason, orderSource);
        } catch (err) {
            console.error("Failed to settle bill:", err);
        } finally {
            setOpenReceipt(false);
            setOpenCheckoutDialog(false);
            setDiscount(0);
            setDiscountReason('');
            setServiceChargeEnabled(false);
            setCustomerPhone('');
            setCustomerConsent(false);
        }
    };

    const handleCheckoutSettleOnly = () => {
        confirmReceiptSettle();
    };

    const handleBtPrint = async () => {
        try {
            await BluetoothPrinterManager.printReceiptCanvas(settledOrderDetails, currentLanguage);
            confirmReceiptSettle();
        } catch (e) {
            console.warn("Direct printing failed:", e);
            showToast("⚠️ ब्लूटूथ प्रिंटर एरर: " + (e.message || 'प्रिंटर कनेक्ट नाही'));
        }
    };

    const handleBtKOTPrint = async () => {
        try {
            await BluetoothPrinterManager.printKOTCanvas(kotDetails, currentLanguage);
            setOpenKOTPreview(false);
        } catch (e) {
            console.warn("Direct printing failed:", e);
            showToast("⚠️ ब्लूटूथ प्रिंटर एरर: " + (e.message || 'प्रिंटर कनेक्ट नाही'));
        }
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            height: { xs: 'auto', md: 'calc(100vh - 70px)' }, 
            overflowY: { xs: 'auto', md: 'hidden' },
            bgcolor: '#f8fafc',
            width: '100%' 
        }}>
            {/* Left side: Menu items grid */}
            <Box sx={{ 
                flexGrow: 1, 
                p: { xs: 2, md: 3 }, 
                display: 'flex', 
                flexDirection: 'column', 
                overflow: 'hidden', 
                width: { xs: '100%', md: '70%' } 
            }}>
                {/* Order Channel Source Selector (Dine-In / Zomato / Swiggy / Takeaway / Delivery) */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569', mr: 0.5 }}>
                        {currentLanguage === 'mr' ? 'ऑर्डर चॅनेल:' : 'Order Channel:'}
                    </Typography>

                    {isPhysicalTable ? (
                        <>
                            <Chip
                                icon={<LockIcon sx={{ fontSize: '15px !important', color: '#ffffff !important' }} />}
                                label={`🍽️ ${currentLanguage === 'mr' ? 'डायन-इन टेबल' : 'Dine-In Table'}: Table ${selectedPhysicalTable?.number || selectedTableId}`}
                                size="small"
                                sx={{
                                    fontWeight: 900, fontSize: '0.8rem', height: 30, px: 0.5,
                                    bgcolor: '#008751',
                                    color: '#ffffff',
                                    boxShadow: '0 2px 6px rgba(0, 135, 81, 0.3)'
                                }}
                            />
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setActiveView('tables')}
                                startIcon={<ArrowBackIcon sx={{ fontSize: '15px !important' }} />}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    py: 0.3,
                                    px: 1.2,
                                    height: 30,
                                    borderRadius: 2,
                                    borderColor: '#cbd5e1',
                                    color: '#475569',
                                    bgcolor: '#ffffff',
                                    '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' }
                                }}
                            >
                                {currentLanguage === 'mr' ? 'फ्लोअर प्लॅन / टेबल बदला' : 'Change Table (Floor)'}
                            </Button>

                            <Tooltip title={currentLanguage === 'mr' ? 'या टेबलची ऑर्डर सुरू आहे. चॅनेल बदलण्यासाठी प्रथम Floor वरून टेबल बदला.' : 'Table order is active. Switch from Floor Plan.'}>
                                <Box sx={{ display: 'flex', gap: 0.8, opacity: 0.35, pointerEvents: 'none', filter: 'grayscale(0.8)' }}>
                                    <Chip label="📦 Takeaway" size="small" sx={{ height: 28, fontSize: '0.75rem', fontWeight: 700 }} />
                                    <Chip label="🔴 Zomato" size="small" sx={{ height: 28, fontSize: '0.75rem', fontWeight: 700 }} />
                                    <Chip label="🟠 Swiggy" size="small" sx={{ height: 28, fontSize: '0.75rem', fontWeight: 700 }} />
                                    <Chip label="🛵 Delivery" size="small" sx={{ height: 28, fontSize: '0.75rem', fontWeight: 700 }} />
                                </Box>
                            </Tooltip>
                        </>
                    ) : (
                        <>
                            <Chip
                                label={`🍽️ ${currentLanguage === 'mr' ? 'Dine-In (फ्लोअर वरून निवडा ➔)' : 'Dine-In (Select on Floor ➔)'}`}
                                size="small"
                                onClick={() => {
                                    showToast(currentLanguage === 'mr' ? 'डायन-इन साठी कृपया फ्लोअरवरून टेबल निवडा!' : 'Please select table from Floor Plan!');
                                    setActiveView('tables');
                                }}
                                sx={{
                                    fontWeight: 800, fontSize: '0.75rem', height: 28,
                                    bgcolor: '#ffffff',
                                    color: '#008751',
                                    border: '1.5px dashed #008751',
                                    '&:hover': { bgcolor: '#f0fdf4' },
                                    cursor: 'pointer'
                                }}
                            />
                            <Chip
                                label="📦 Counter Takeaway"
                                size="small"
                                onClick={() => { setOrderSource('takeaway'); selectTable('counter'); }}
                                sx={{
                                    fontWeight: 800, fontSize: '0.75rem', height: 28,
                                    bgcolor: orderSource === 'takeaway' ? '#475569' : '#ffffff',
                                    color: orderSource === 'takeaway' ? '#ffffff' : '#475569',
                                    border: '1px solid #475569',
                                    cursor: 'pointer'
                                }}
                            />
                            <Chip
                                label="🔴 Zomato Order"
                                size="small"
                                onClick={() => { setOrderSource('zomato'); selectTable('counter'); }}
                                sx={{
                                    fontWeight: 800, fontSize: '0.75rem', height: 28,
                                    bgcolor: orderSource === 'zomato' ? '#cb202d' : '#ffffff',
                                    color: orderSource === 'zomato' ? '#ffffff' : '#cb202d',
                                    border: '1px solid #cb202d',
                                    cursor: 'pointer'
                                }}
                            />
                            <Chip
                                label="🟠 Swiggy Order"
                                size="small"
                                onClick={() => { setOrderSource('swiggy'); selectTable('counter'); }}
                                sx={{
                                    fontWeight: 800, fontSize: '0.75rem', height: 28,
                                    bgcolor: orderSource === 'swiggy' ? '#fc8019' : '#ffffff',
                                    color: orderSource === 'swiggy' ? '#ffffff' : '#fc8019',
                                    border: '1px solid #fc8019',
                                    cursor: 'pointer'
                                }}
                            />
                            <Chip
                                label="🛵 Direct Delivery"
                                size="small"
                                onClick={() => { setOrderSource('delivery'); selectTable('counter'); }}
                                sx={{
                                    fontWeight: 800, fontSize: '0.75rem', height: 28,
                                    bgcolor: orderSource === 'delivery' ? '#0284c7' : '#ffffff',
                                    color: orderSource === 'delivery' ? '#ffffff' : '#0284c7',
                                    border: '1px solid #0284c7',
                                    cursor: 'pointer'
                                }}
                            />

                            {(orderSource === 'zomato' || orderSource === 'swiggy') && (
                                <TextField
                                    size="small"
                                    placeholder={orderSource === 'zomato' ? 'Zomato Order ID (#ZOM-xxxx)' : 'Swiggy Order ID (#SWG-xxxx)'}
                                    value={aggregatorOrderId}
                                    onChange={(e) => setAggregatorOrderId(e.target.value)}
                                    sx={{ ml: 'auto', maxWidth: 220, bgcolor: '#ffffff', '& input': { py: 0.5, fontSize: '0.8rem', fontWeight: 700 } }}
                                />
                            )}
                        </>
                    )}
                </Box>

                {/* Search and Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder={t('pos-search-placeholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        inputProps={{
                            autoComplete: 'off',
                            autoCorrect: 'off',
                            autoCapitalize: 'none',
                            spellCheck: 'false',
                            inputMode: 'search',
                            style: { touchAction: 'manipulation' }
                        }}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                        }}
                        sx={{ bgcolor: '#ffffff', borderRadius: 2, flexGrow: 1, '& fieldset': { borderColor: '#d1d5db' } }}
                    />
                    
                    {/* Table Control: Locked Table Badge for Dine-In vs Counter Badge for Direct POS */}
                    {isPhysicalTable ? (
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            bgcolor: '#f0fdf4', 
                            border: '1.5px solid #86efac', 
                            borderRadius: 2, 
                            px: 1.5, 
                            py: 0.8,
                            minHeight: 40,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                        }}>
                            <LockIcon sx={{ fontSize: 18, color: '#16a34a' }} />
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#15803d', whiteSpace: 'nowrap' }}>
                                Table {selectedPhysicalTable?.number || selectedTableId}
                            </Typography>
                            {selectedPhysicalTable?.section_id && (
                                <Chip 
                                    label={sections?.find(s => String(s.id) === String(selectedPhysicalTable.section_id))?.name || 'Main'} 
                                    size="small" 
                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800, bgcolor: '#dcfce7', color: '#166534' }} 
                                />
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            bgcolor: '#f8fafc', 
                            border: '1px dashed #cbd5e1', 
                            borderRadius: 2, 
                            px: 1.5, 
                            py: 0.8,
                            minHeight: 40
                        }}>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#64748b', whiteSpace: 'nowrap' }}>
                                📦 {currentLanguage === 'mr' ? 'काउंटर / पार्सल मोड' : 'Counter / Parcel Mode'}
                            </Typography>
                        </Box>
                    )}
                    <Button
                        variant="outlined"
                        onClick={async () => {
                            if (!navigator.onLine) {
                                showToast(currentLanguage === 'mr' ? 'तुम्ही ऑफलाईन आहात. नेट तपासा!' : 'You are offline. Check internet!');
                                return;
                            }
                            showToast(currentLanguage === 'mr' ? 'मेनू रिफ्रेश होत आहे...' : 'Refreshing menu...');
                            await refreshMenuCatalog();
                            showToast(currentLanguage === 'mr' ? 'मेनू यशस्वीरित्या रिफ्रेश झाला!' : 'Menu refreshed successfully!');
                        }}
                        sx={{ height: 40, whiteSpace: 'nowrap', borderColor: '#d1d5db', color: '#4b5563', '&:hover': { bgcolor: '#f9fafb' }, textTransform: 'none', fontWeight: 600 }}
                        startIcon={<SyncIcon />}
                    >
                        {currentLanguage === 'mr' ? 'रिफ्रेश मेनू' : 'Refresh Menu'}
                    </Button>
                </Box>

                {/* Category selectors */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3, overflowX: 'auto', pb: 1, minHeight: 45, alignItems: 'center' }}>
                    <Chip
                        label={currentLanguage === 'mr' ? '📌 पिन केलेले' : '📌 Pinned'}
                        onClick={() => setSelectedCategory('pinned')}
                        variant={selectedCategory === 'pinned' ? 'filled' : 'outlined'}
                        sx={{
                            fontWeight: 800, fontSize: '0.9rem', px: 2, height: 40,
                            bgcolor: selectedCategory === 'pinned' ? '#ea580c' : '#ffffff',
                            color: selectedCategory === 'pinned' ? '#ffffff' : '#ea580c',
                            borderColor: selectedCategory === 'pinned' ? '#ea580c' : '#fdba74',
                            '&:hover': { bgcolor: selectedCategory === 'pinned' ? '#c2410c' : '#fff7ed' }
                        }}
                    />
                    <Chip
                        label={t('cat-all')}
                        onClick={() => setSelectedCategory('all')}
                        variant={selectedCategory === 'all' ? 'filled' : 'outlined'}
                        sx={{
                            fontWeight: 800, fontSize: '0.9rem', px: 2, height: 40,
                            bgcolor: selectedCategory === 'all' ? '#008751' : '#ffffff',
                            color: selectedCategory === 'all' ? '#ffffff' : '#4b5563',
                            borderColor: '#d1d5db',
                            '&:hover': { bgcolor: selectedCategory === 'all' ? '#006f42' : '#f9fafb' }
                        }}
                    />
                    {(() => {
                        const sortedCatList = [...activeCategories].sort((a, b) => (parseInt(a.display_order) || 0) - (parseInt(b.display_order) || 0));
                        return sortedCatList.map(cat => (
                            <Chip
                                key={cat.id}
                                label={currentLanguage === 'mr' ? (cat.name_mr || cat.name) : cat.name}
                                onClick={() => setSelectedCategory(cat.id)}
                                variant={selectedCategory === cat.id ? 'filled' : 'outlined'}
                                sx={{
                                    fontWeight: 800, fontSize: '0.9rem', px: 2, height: 40,
                                    bgcolor: selectedCategory === cat.id ? '#008751' : '#ffffff',
                                    color: selectedCategory === cat.id ? '#ffffff' : '#4b5563',
                                    borderColor: '#d1d5db',
                                    '&:hover': { bgcolor: selectedCategory === cat.id ? '#006f42' : '#f9fafb' }
                                }}
                            />
                        ));
                    })()}
                </Box>

                {/* Menu Card Grid */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-start' }}>
                        {filteredMenu.map(item => {
                            const isVeg = checkIsVeg(item);
                            if (posCardLayout === 'compact1') {
                                const headerColor = isVeg ? '#16a34a' : '#dc2626';

                                return (
                                    <Box key={item.id} sx={{ width: 175, height: 135, flexShrink: 0, flexGrow: 0 }}>
                                        <Card
                                            onClick={() => handleAddToCartAuto(item)}
                                            sx={{
                                                width: 175,
                                                minWidth: 175,
                                                maxWidth: 175,
                                                height: 135,
                                                minHeight: 135,
                                                maxHeight: 135,
                                                borderRadius: '12px',
                                                border: '1.5px solid #cbd5e1',
                                                bgcolor: '#ffffff',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                cursor: 'pointer',
                                                overflow: 'hidden',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                                                '&:hover': {
                                                    borderColor: headerColor,
                                                    boxShadow: `0 6px 16px ${isVeg ? 'rgba(22,163,74,0.2)' : 'rgba(220,38,38,0.2)'}`,
                                                    transform: 'translateY(-2px)'
                                                },
                                                transition: 'all 0.15s ease-in-out'
                                            }}
                                        >
                                            {/* Top Half: Solid Green/Red Header */}
                                            <Box sx={{
                                                bgcolor: headerColor,
                                                p: 1.2,
                                                flexGrow: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                position: 'relative'
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 3, width: '100%' }}>
                                                    <Typography sx={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 110 }}>
                                                        {getCategoryName(item)}
                                                    </Typography>
                                                    <Box 
                                                        title={isVeg ? 'Veg' : 'Non-Veg'}
                                                        sx={{ 
                                                            width: 10, height: 10, borderRadius: '2px', 
                                                            border: '1.5px solid #ffffff', 
                                                            bgcolor: '#ffffff', 
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: isVeg ? '#16a34a' : '#dc2626' }} />
                                                    </Box>
                                                </Box>

                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); togglePinItem(item.id); }}
                                                    sx={{
                                                        position: 'absolute', top: 5, right: 5,
                                                        bgcolor: pinnedItems.includes(item.id) ? '#ea580c' : 'rgba(255,255,255,0.25)',
                                                        color: '#ffffff',
                                                        p: '2px',
                                                        border: '1px solid rgba(255,255,255,0.4)',
                                                        '&:hover': { bgcolor: pinnedItems.includes(item.id) ? '#c2410c' : 'rgba(255,255,255,0.4)' }
                                                    }}
                                                >
                                                    {pinnedItems.includes(item.id) ? <PushPinIcon sx={{ fontSize: 12 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 12 }} />}
                                                </IconButton>

                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 900,
                                                        color: '#ffffff',
                                                        fontSize: '0.85rem',
                                                        lineHeight: 1.25,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        mt: 'auto'
                                                    }}
                                                >
                                                    {getDisplayName(item)}
                                                </Typography>
                                            </Box>

                                            {/* Bottom Half: White Rate & Circular Add Button */}
                                            <Box sx={{
                                                bgcolor: '#ffffff',
                                                px: 1.2,
                                                py: 0.8,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justify: 'space-between',
                                                height: 42,
                                                flexShrink: 0
                                            }}>
                                                <Typography sx={{ fontWeight: 900, fontSize: '1rem', color: headerColor }}>
                                                    ₹{item.price}
                                                </Typography>

                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handleAddToCartAuto(item); }}
                                                    sx={{
                                                        width: 28,
                                                        height: 28,
                                                        bgcolor: headerColor,
                                                        color: '#ffffff',
                                                        fontWeight: 900,
                                                        fontSize: '0.95rem',
                                                        '&:hover': { bgcolor: headerColor, transform: 'scale(1.08)' }
                                                    }}
                                                >
                                                    +
                                                </IconButton>
                                            </Box>
                                        </Card>
                                    </Box>
                                );
                            }

                            if (posCardLayout === 'compact2') {
                                const isVeg = checkIsVeg(item);
                                const accentColor = isVeg ? '#16a34a' : '#dc2626';

                                return (
                                    <Box key={item.id} sx={{ width: 175, height: 135, flexShrink: 0, flexGrow: 0 }}>
                                        <Card
                                            onClick={() => handleAddToCartAuto(item)}
                                            sx={{
                                                width: 175,
                                                minWidth: 175,
                                                maxWidth: 175,
                                                height: 135,
                                                minHeight: 135,
                                                maxHeight: 135,
                                                borderRadius: '10px',
                                                border: '1px solid #e2e8f0',
                                                borderLeft: `4px solid ${accentColor}`,
                                                bgcolor: '#ffffff',
                                                p: 1.5,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer',
                                                position: 'relative',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                                '&:hover': {
                                                    borderColor: accentColor,
                                                    boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                                                    transform: 'translateY(-2px)'
                                                },
                                                transition: 'all 0.15s ease-in-out'
                                            }}
                                        >
                                            <Box sx={{ pr: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.5 }}>
                                                    <CircleIcon sx={{
                                                        fontSize: 10,
                                                        color: accentColor,
                                                        border: `1.5px solid ${isVeg ? '#15803d' : '#b91c1c'}`,
                                                        p: '0.5px',
                                                        borderRadius: '2px'
                                                    }} />
                                                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: isVeg ? '#15803d' : '#b91c1c', textTransform: 'uppercase' }}>
                                                        {isVeg ? 'VEG' : 'NON-VEG'}
                                                    </Typography>
                                                </Box>

                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 800,
                                                        color: '#0f172a',
                                                        fontSize: '0.88rem',
                                                        lineHeight: 1.3,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    {getDisplayName(item)}
                                                </Typography>
                                            </Box>

                                            <IconButton
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); togglePinItem(item.id); }}
                                                sx={{
                                                    position: 'absolute', top: 8, right: 8,
                                                    bgcolor: pinnedItems.includes(item.id) ? '#ea580c' : '#f8fafc',
                                                    color: pinnedItems.includes(item.id) ? '#ffffff' : '#94a3b8',
                                                    p: '3px',
                                                    border: '1px solid #e2e8f0',
                                                    '&:hover': { bgcolor: pinnedItems.includes(item.id) ? '#c2410c' : '#f1f5f9' }
                                                }}
                                            >
                                                {pinnedItems.includes(item.id) ? <PushPinIcon sx={{ fontSize: 13 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 13 }} />}
                                            </IconButton>

                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5, borderTop: '1px dashed #f1f5f9' }}>
                                                <Typography sx={{ fontWeight: 900, fontSize: '0.98rem', color: '#008751' }}>
                                                    ₹{item.price}
                                                </Typography>

                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handleAddToCartAuto(item); }}
                                                    sx={{
                                                        width: 28,
                                                        height: 28,
                                                        bgcolor: '#008751',
                                                        color: '#ffffff',
                                                        fontWeight: 900,
                                                        fontSize: '0.95rem',
                                                        '&:hover': { bgcolor: '#006f42', transform: 'scale(1.08)' }
                                                    }}
                                                >
                                                    +
                                                </IconButton>
                                            </Box>
                                        </Card>
                                    </Box>
                                );
                            }

                            return (
                                <Box key={item.id} sx={{ width: 175, height: 210, flexShrink: 0, flexGrow: 0 }}>
                                    <Card 
                                        onClick={() => handleAddToCartAuto(item)}
                                        sx={{ 
                                            width: 175,
                                            minWidth: 175,
                                            maxWidth: 175,
                                            height: 210,
                                            minHeight: 210,
                                            maxHeight: 210,
                                            borderRadius: 3, 
                                            cursor: 'pointer',
                                            position: 'relative',
                                            border: '1px solid #e5e7eb',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                                borderColor: '#008751'
                                            },
                                            transition: 'all 0.2s ease'
                                        }}
                                    >
                                    <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); togglePinItem(item.id); }}
                                        sx={{
                                            position: 'absolute', top: 8, right: 8, bgcolor: pinnedItems.includes(item.id) ? '#ea580c' : 'rgba(255,255,255,0.85)',
                                            color: pinnedItems.includes(item.id) ? '#ffffff' : '#64748b',
                                            p: '3px',
                                            zIndex: 2,
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                                            '&:hover': { bgcolor: pinnedItems.includes(item.id) ? '#c2410c' : '#ffffff' }
                                        }}
                                    >
                                        {pinnedItems.includes(item.id) ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />}
                                    </IconButton>
                                    <CardMedia
                                        component="img"
                                        height="100"
                                        image={item.image_url || item.image || 'images/default_food.png'}
                                        alt={getDisplayName(item)}
                                        sx={{ objectFit: 'cover' }}
                                    />
                                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                            <CircleIcon sx={{ 
                                                fontSize: 10, 
                                                color: isVeg ? '#10b981' : '#ef4444',
                                                border: `1px solid ${isVeg ? '#047857' : '#b91c1c'}`,
                                                p: '1px',
                                                borderRadius: '3px'
                                            }} />
                                            <Typography variant="caption" sx={{ fontWeight: 800, color: isVeg ? '#047857' : '#b91c1c' }}>
                                                {isVeg ? t('pos-veg-tag') : t('pos-nonveg-tag')}
                                            </Typography>
                                        </Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#111827', lineHeight: 1.2, mb: 1, height: 34, overflow: 'hidden' }}>
                                            {getDisplayName(item)}
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#008751' }}>
                                            ₹{item.price}
                                        </Typography>
                                        {subscribedModules.includes('compliance') && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                                <Chip 
                                                    label={getComplianceDetails(item).calories} 
                                                    size="small" 
                                                    sx={{ bgcolor: '#eff6ff', color: '#1e40af', fontSize: '0.55rem', height: 16, fontWeight: 800 }}
                                                />
                                                <Chip 
                                                    label={`Allergens: ${getComplianceDetails(item).allergens}`} 
                                                    size="small" 
                                                    sx={{ bgcolor: '#fff7ed', color: '#c2410c', fontSize: '0.55rem', height: 16, fontWeight: 800 }}
                                                />
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Box>
                        );
                    })}
                    </Box>
                </Box>

                {/* 2-Button Shortcut Bar in Menu Section (Inline at bottom of Menu) */}
                <Box sx={{ mt: 1, flexShrink: 0 }}>
                    <BottomNavigationBar isInlineMenuFooter={true} onMoreClick={() => setSidebarMobileOpen(true)} />
                </Box>
            </Box>

            {/* Right side: Active Cart Order checkout panel */}
            <Box sx={{ 
                width: { xs: '100%', md: '32%' }, 
                minWidth: { xs: '100%', md: 380 }, 
                bgcolor: '#ffffff', 
                borderLeft: '2px solid #cbd5e1',
                boxShadow: '-8px 0 25px -5px rgba(0, 0, 0, 0.06)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                maxHeight: '100%',
                overflow: 'hidden',
                zIndex: 2
            }}>
                {/* Cart Header */}
                <Box sx={{ 
                    p: 2, 
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', 
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justify: 'space-between',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                    flexShrink: 0
                }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#ffffff', fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                            🛒 {directOrder ? (directOrder.type || `Parcel (${directOrder.id})`) : (selectedTableId ? (selectedTableId === 'counter' ? 'Counter Parcel' : `Table ${table?.number || selectedTableId}`) : t('pos-select-table'))}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)', fontWeight: 700, display: 'block', mt: 0.2 }}>
                            {activeOrder ? `Order #${activeOrder.id.replace('ORD-', '')} • ${activeOrder.status.toUpperCase()}` : t('pos-no-active-order')}
                        </Typography>
                    </Box>
                    <Chip 
                        label={activeCart.reduce((sum, item) => sum + item.quantity, 0) + ' Items'} 
                        size="small" 
                        sx={{ bgcolor: '#008751', color: '#ffffff', fontWeight: 900, fontSize: '0.75rem', px: 0.5 }} 
                    />
                </Box>

                {/* Cart items list with forced vertical scrollbar */}
                <Box sx={{ 
                    flex: '1 1 0%', 
                    minHeight: 0,
                    overflowY: 'auto', 
                    p: 2, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1.2, 
                    bgcolor: '#f8fafc',
                    '&::-webkit-scrollbar': { width: '8px', display: 'block' },
                    '&::-webkit-scrollbar-track': { background: '#e2e8f0', borderRadius: '4px' },
                    '&::-webkit-scrollbar-thumb': { background: '#008751', borderRadius: '4px' },
                    '&::-webkit-scrollbar-thumb:hover': { background: '#006f42' }
                }}>
                    {activeCart.length === 0 ? (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justify: 'center', 
                            height: '100%',
                            color: '#94a3b8',
                            gap: 1.5,
                            py: 6
                        }}>
                            <RestaurantIcon sx={{ fontSize: 52, color: '#cbd5e1' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#64748b', textAlign: 'center' }}>
                                Select a table or parcel counter and tap dishes to build cart
                            </Typography>
                        </Box>
                    ) : (
                        activeCart.map(item => {
                            const menuItem = (menuItems || []).find(m => String(m.id) === String(item.id) || String(m.id) === String(item.menu_item_id));
                            const isVeg = menuItem?.veg ?? item.veg;
                            const displayName = getDisplayName(menuItem) || (currentLanguage === 'mr' ? (item.name_mr || item.nameMr || item.name) : (item.name || item.name_mr || item.nameMr)) || 'Dish';

                            return (
                                <Card key={item.id} elevation={0} sx={{ 
                                    flexShrink: 0,
                                    borderRadius: 2.5, 
                                    border: '1.5px solid #cbd5e1', 
                                    p: 1.5, 
                                    bgcolor: '#ffffff',
                                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.02)',
                                    transition: 'all 0.15s ease',
                                    '&:hover': { borderColor: '#008751', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.8 }}>
                                        <Box sx={{ flexGrow: 1, pr: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#0f172a', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 0.8, lineHeight: 1.25 }}>
                                                <CircleIcon sx={{ 
                                                    fontSize: 10, 
                                                    color: isVeg ? '#16a34a' : '#dc2626',
                                                    border: `1.5px solid ${isVeg ? '#15803d' : '#b91c1c'}`,
                                                    p: '0.5px',
                                                    borderRadius: '2px'
                                                }} />
                                                {displayName}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#008751', fontWeight: 800, mt: 0.2, fontSize: '0.85rem' }}>
                                                ₹{item.price} <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>each</span>
                                            </Typography>
                                        </Box>

                                        {/* Touch-friendly Quantity Controller */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, bgcolor: '#f1f5f9', p: '2px 5px', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                            <IconButton 
                                                size="small" 
                                                onClick={() => removeFromCart(item.id)}
                                                sx={{ width: 28, height: 28, bgcolor: '#ffffff', color: '#0f172a', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', '&:hover': { bgcolor: '#fee2e2', color: '#dc2626' } }}
                                            >
                                                <RemoveIcon sx={{ fontSize: 16, fontWeight: 900 }} />
                                            </IconButton>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 900, width: 24, textAlign: 'center', color: '#008751', fontSize: '1rem' }}>
                                                {item.quantity}
                                            </Typography>
                                            <IconButton 
                                                size="small" 
                                                onClick={() => handleAddToCartAuto(menuItem)}
                                                sx={{ width: 28, height: 28, bgcolor: '#008751', color: '#ffffff', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', '&:hover': { bgcolor: '#006f42' } }}
                                            >
                                                <AddIcon sx={{ fontSize: 16, fontWeight: 900 }} />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    
                                    <TextField
                                        size="small"
                                        placeholder="Cook notes (उदा. कमी तिखट)..."
                                        value={item.remarks}
                                        onChange={(e) => updateRemarks(item.id, e.target.value)}
                                        inputProps={{
                                            autoComplete: 'off',
                                            autoCorrect: 'off',
                                            spellCheck: 'false',
                                            style: { touchAction: 'manipulation', padding: '5px 8px', fontSize: '0.78rem', fontWeight: 600 }
                                        }}
                                        sx={{ 
                                            mt: 0.3,
                                            width: '100%',
                                            bgcolor: '#f8fafc',
                                            borderRadius: 1.5,
                                            '& fieldset': { borderColor: '#e2e8f0' }
                                        }}
                                    />
                                </Card>
                            );
                        })
                    )}
                </Box>

                {/* Cart summary & Prominent Bill Total */}
                <Box sx={{ p: 2, borderTop: '2px solid #e2e8f0', bgcolor: '#ffffff', flexShrink: 0, boxShadow: '0 -4px 15px rgba(0,0,0,0.03)' }}>
                    <Grid container spacing={1} sx={{ mb: 1.5 }}>
                        <Grid item xs={6}>
                            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.88rem' }}>{t('pos-subtotal')}</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.92rem' }}>₹{subtotal.toFixed(2)}</Typography>
                        </Grid>

                        {enableGst && (
                            <>
                                <Grid item xs={6}>
                                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.88rem' }}>{t('pos-tax')}</Typography>
                                </Grid>
                                <Grid item xs={6} textAlign="right">
                                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.92rem' }}>₹{tax.toFixed(2)}</Typography>
                                </Grid>
                            </>
                        )}

                        <Grid item xs={6} display="flex" alignItems="center">
                            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.88rem' }}>{t('pos-discount')}</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                            <TextField
                                size="small"
                                type="number"
                                value={discount}
                                onChange={(e) => setDiscount(Math.max(0, parseFloat(e.target.value) || 0))}
                                disabled={lockedActions && lockedActions.includes('apply_discount')}
                                inputProps={{ style: { touchAction: 'manipulation' } }}
                                sx={{ 
                                    width: 100,
                                    bgcolor: '#ffffff',
                                    '& input': { py: 0.3, px: 1, textAlign: 'right', fontSize: '0.82rem', fontWeight: 800 }
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* RIGHT-ALIGNED TOTAL BILL BOX */}
                    <Box sx={{ 
                        background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', 
                        border: '2px solid #059669', 
                        borderRadius: 2.5, 
                        p: 1.5, 
                        mb: 2, 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'flex-end', 
                        justify: 'center',
                        textAlign: 'right',
                        boxShadow: '0 4px 12px rgba(5, 150, 105, 0.12)'
                    }}>
                        <Typography variant="caption" sx={{ fontWeight: 900, color: '#047857', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'block', fontSize: '0.75rem' }}>
                            {currentLanguage === 'mr' ? 'एकूण देय रक्कम' : 'TOTAL BILL AMOUNT'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.2 }}>
                            {enableGst && (
                                <Chip 
                                    label="GST INCL" 
                                    size="small" 
                                    sx={{ bgcolor: '#059669', color: '#ffffff', fontWeight: 900, fontSize: '0.6rem', height: 18 }} 
                                />
                            )}
                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#065f46', lineHeight: 1, fontSize: '1.8rem' }}>
                                ₹{finalTotal.toFixed(2)}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Action Buttons */}
                    <Grid container spacing={1.2}>
                        <Grid item xs={6}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleSendKOTDirect}
                                disabled={activeCart.length === 0}
                                sx={{ 
                                    py: 1.1, 
                                    fontWeight: 900, 
                                    fontSize: '0.85rem',
                                    textTransform: 'none', 
                                    borderRadius: 2.5,
                                    bgcolor: '#d97706',
                                    color: '#ffffff',
                                    boxShadow: '0 4px 12px rgba(217, 119, 6, 0.3)',
                                    '&:hover': { bgcolor: '#b45309' }
                                }}
                            >
                                🔥 {currentLanguage === 'mr' ? 'KOT पाठवा (Save KOT)' : 'Send KOT To Kitchen'}
                            </Button>
                        </Grid>
                        <Grid item xs={6}>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={handleKOTClick}
                                disabled={activeCart.length === 0}
                                sx={{ 
                                    py: 1.1, 
                                    fontWeight: 900, 
                                    fontSize: '0.85rem',
                                    textTransform: 'none', 
                                    borderRadius: 2.5,
                                    border: '2px solid #0f172a',
                                    color: '#0f172a',
                                    '&:hover': { border: '2px solid #0f172a', bgcolor: '#f1f5f9' }
                                }}
                            >
                                🔍 KOT Print
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="outlined"
                                color="error"
                                fullWidth
                                onClick={() => setOpenCancelDialog(true)}
                                disabled={!activeOrder}
                                sx={{ 
                                    py: 0.8, 
                                    fontWeight: 900, 
                                    fontSize: '0.85rem',
                                    textTransform: 'none', 
                                    borderRadius: 2.5,
                                    border: '1.5px solid #dc2626'
                                }}
                            >
                                🚫 Cancel Order
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={() => setOpenCheckoutDialog(true)}
                                disabled={activeCart.length === 0 && !activeOrder}
                                sx={{ 
                                    py: 1.4, 
                                    fontWeight: 900, 
                                    fontSize: '1.05rem', 
                                    textTransform: 'none', 
                                    bgcolor: '#008751', 
                                    color: '#ffffff',
                                    boxShadow: '0 6px 18px rgba(0, 135, 81, 0.3)', 
                                    '&:hover': { bgcolor: '#006f42', boxShadow: '0 8px 22px rgba(0, 135, 81, 0.4)', transform: 'translateY(-1px)' }, 
                                    borderRadius: 2.5,
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                💳 {currentLanguage === 'mr' ? 'चेकआउट (सेटल व प्रिंट)' : 'Checkout (Settle & Print)'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            {/* Cancel Order Dialog */}
            <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)} PaperProps={{ sx: { borderRadius: 3, width: 400 } }}>
                <DialogTitle sx={{ fontWeight: 900, color: '#b91c1c' }}>Cancel Order</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: '#475569' }}>
                        Are you sure you want to cancel this order? This action cannot be undone.
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        label="Reason for Cancellation"
                        placeholder="e.g. Customer left, Wrong order..."
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        required
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenCancelDialog(false)} color="inherit">Close</Button>
                    <Button onClick={handleCancelConfirm} variant="contained" color="error" disabled={cancelReason.trim() === ''}>
                        Confirm Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Checkout Dialog */}
            <Dialog open={openCheckoutDialog} onClose={() => setOpenCheckoutDialog(false)} PaperProps={{ sx: { borderRadius: 3, width: 450, p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 900, pb: 1 }}>
                    Checkout & Payment
                    <Typography variant="subtitle2" color="text.secondary">Order Total: ₹{finalTotal.toFixed(2)}</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#334155', display: 'block', mb: 1, mt: 1 }}>
                        Payment Method
                    </Typography>
                    <ToggleButtonGroup
                        value={paymentMethod}
                        exclusive
                        onChange={(e, val) => {
                            if (val && !['zomato', 'swiggy'].includes(orderSource)) {
                                setPaymentMethod(val);
                            }
                        }}
                        fullWidth
                        size="small"
                        sx={{ mb: 2, '& .MuiToggleButton-root': { py: 1, fontWeight: 700, fontSize: '0.8rem' } }}
                    >
                        {orderSource === 'zomato' ? (
                            <ToggleButton value="zomato" disabled sx={{ color: '#cb202d !important', borderColor: '#cb202d !important', bgcolor: '#fff5f5 !important' }}>Zomato Payout</ToggleButton>
                        ) : orderSource === 'swiggy' ? (
                            <ToggleButton value="swiggy" disabled sx={{ color: '#fc8019 !important', borderColor: '#fc8019 !important', bgcolor: '#fff7ed !important' }}>Swiggy Payout</ToggleButton>
                        ) : (
                            [
                                <ToggleButton key="cash" value="cash">{t('pos-cash')}</ToggleButton>,
                                <ToggleButton key="upi" value="upi">{t('pos-upi')}</ToggleButton>,
                                <ToggleButton key="split" value="split">Split</ToggleButton>,
                                <ToggleButton key="free" value="free">Free / Disc</ToggleButton>
                            ]
                        )}
                    </ToggleButtonGroup>

                    {paymentMethod === 'split' && (
                        <Box sx={{ mt: 1, mb: 2, p: 1.5, border: '1px solid #cbd5e1', borderRadius: 2, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField size="small" label="Cash (₹)" type="number" value={splitCashAmount} onChange={(e) => { const val = Math.max(0, parseFloat(e.target.value) || 0); setSplitCashAmount(val); setSplitUpiAmount(Math.max(0, finalTotal - val)); }} sx={{ bgcolor: '#ffffff' }} />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField size="small" label="UPI (₹)" type="number" value={splitUpiAmount} onChange={(e) => { const val = Math.max(0, parseFloat(e.target.value) || 0); setSplitUpiAmount(val); setSplitCashAmount(Math.max(0, finalTotal - val)); }} sx={{ bgcolor: '#ffffff' }} />
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#334155', display: 'block', mb: 1, mt: 2 }}>
                        Discount & Reason
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={4}>
                            <TextField size="small" label="Discount (₹)" type="number" value={discount} onChange={(e) => setDiscount(Math.max(0, parseFloat(e.target.value) || 0))} disabled={lockedActions && lockedActions.includes('apply_discount')} sx={{ bgcolor: '#ffffff' }} fullWidth />
                        </Grid>
                        <Grid item xs={8}>
                            <TextField size="small" label="Reason for Discount" placeholder="e.g. Owner guest, Staff meal..." value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} sx={{ bgcolor: '#ffffff' }} fullWidth />
                        </Grid>
                    </Grid>

                    {subscribedModules.includes('compliance') && (
                        <Box sx={{ mt: 1, p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#f8fafc' }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569', display: 'block', mb: 1 }}>
                                🔒 DPDP ACT DATA PRIVACY CONSENT
                            </Typography>
                            <TextField size="small" label="Customer Phone (CRM)" variant="outlined" fullWidth value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} sx={{ bgcolor: '#ffffff', mb: 1 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Checkbox size="small" checked={customerConsent} onChange={(e) => setCustomerConsent(e.target.checked)} sx={{ p: 0, mr: 0.5 }} />
                                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 600 }}>I agree to receive promotional SMS.</Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button variant="contained" fullWidth onClick={handleSettleClick} sx={{ py: 1.2, fontWeight: 800, bgcolor: '#0ea5e9', '&:hover': { bgcolor: '#0284c7' }, textTransform: 'none' }}>
                        Save & Print Receipt (प्रिंट करा)
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                        <Button variant="contained" fullWidth onClick={handleCheckoutSettleOnly} sx={{ py: 1, fontWeight: 800, bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, textTransform: 'none' }}>
                            Save Only (फक्त सेव्ह करा)
                        </Button>
                        <Button variant="outlined" fullWidth onClick={() => setOpenCheckoutDialog(false)} sx={{ py: 1, fontWeight: 800, textTransform: 'none' }}>
                            Cancel
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Settle & Print Receipt Modal Popup */}
            <Dialog 
                open={openReceipt} 
                onClose={() => setOpenReceipt(false)}
                PaperProps={{
                    sx: { borderRadius: 3, width: 400, bgcolor: '#f1f5f9' }
                }}
            >
                <DialogTitle sx={{ fontWeight: 900, display: 'flex', justify: 'space-between', alignItems: 'center', pb: 1, bgcolor: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                    <span>{t('print-receipt')}</span>
                    <IconButton size="small" onClick={() => setOpenReceipt(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                    {receiptPreviewImage ? (
                        <Box id="receipt-paper" sx={{
                            width: '100%',
                            maxWidth: 384,
                            bgcolor: '#fffffa',
                            boxShadow: '0px 8px 24px rgba(0,0,0,0.1)',
                            borderTop: '6px dashed #cbd5e1',
                            borderBottom: '6px dashed #cbd5e1',
                            p: 2,
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <img src={receiptPreviewImage} alt="Receipt Preview" style={{ width: '100%', maxWidth: '100%', height: 'auto', display: 'block' }} />
                        </Box>
                    ) : (
                        <Typography sx={{ p: 4, textAlign: 'center' }}>Generating Preview...</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button 
                                variant="contained" 
                                color="success" 
                                fullWidth 
                                startIcon={<LocalPrintshopIcon />}
                                onClick={() => { printOrderReceipt(settledOrderDetails, currentLanguage); confirmReceiptSettle(); }}
                                sx={{ py: 1.2, fontWeight: 800, textTransform: 'none', borderRadius: 2, bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                            >
                                System Print (सिस्टम प्रिंट)
                            </Button>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                fullWidth 
                                startIcon={<LocalPrintshopIcon />}
                                onClick={handleBtPrint}
                                sx={{ py: 1.2, fontWeight: 800, textTransform: 'none', borderRadius: 2, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
                            >
                                {btDeviceName ? `BT: ${btDeviceName}` : 'Bluetooth Print (ब्लूटूथ)'}
                            </Button>
                        </Box>
                        <Button
                            variant="text"
                            onClick={() => setOpenReceipt(false)}
                            sx={{ fontWeight: 800, textTransform: 'none', color: '#64748b' }}
                        >
                            Cancel / Close
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* KOT Print Preview Modal Popup */}
            <Dialog 
                open={openKOTPreview} 
                onClose={() => setOpenKOTPreview(false)}
                PaperProps={{
                    sx: { borderRadius: 3, width: 400, bgcolor: '#f1f5f9' }
                }}
            >
                <DialogTitle sx={{ fontWeight: 900, display: 'flex', justify: 'space-between', alignItems: 'center', pb: 1, bgcolor: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                    <span>Kitchen Order Ticket (KOT) Preview</span>
                    <IconButton size="small" onClick={() => setOpenKOTPreview(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                    {kotDetails && (
                        <Box id="standard-kot-paper" sx={{
                            width: '100%',
                            maxWidth: 340,
                            bgcolor: '#fffffa', // Thermal paper tint
                            boxShadow: '0px 8px 24px rgba(0,0,0,0.1)',
                            borderTop: '6px dashed #ef4444', // Red border for KOT
                            borderBottom: '6px dashed #ef4444',
                            p: 3,
                            fontFamily: '"Courier New", Courier, monospace',
                            color: '#1e293b'
                        }}>
                            <Box textAlign="center" sx={{ mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#ef4444', fontFamily: '"Courier New", Courier, monospace', letterSpacing: 1 }}>
                                    KITCHEN ORDER TICKET
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 900, fontFamily: '"Courier New", Courier, monospace', mt: 0.5, fontSize: '1.25rem', bgcolor: '#ef4444', color: '#ffffff', py: 0.5, borderRadius: 1.5 }}>
                                    TABLE {kotDetails.tableNumber}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ fontSize: '0.8rem', my: 1.5 }}>
                                <div>KOT No: {kotDetails.id}</div>
                                <div>Time: {new Date(kotDetails.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                <div>Waiter: {kotDetails.waiterName}</div>
                            </Box>
                            
                            <Box sx={{ borderBottom: '2px solid #ef4444', my: 1 }} />
                            
                            {/* Items Header */}
                            <Box sx={{ display: 'flex', justify: 'space-between', fontWeight: 'bold', fontSize: '0.85rem', mb: 0.5, color: '#ef4444' }}>
                                <span style={{ flexGrow: 1 }}>Item Description</span>
                                <span style={{ width: 60, textAlign: 'right' }}>Qty</span>
                            </Box>
                            <Box sx={{ borderBottom: '2px solid #ef4444', mb: 1 }} />
                            
                            {/* Items list */}
                            {kotDetails.items.map((item, idx) => (
                                <Box key={idx} sx={{ py: 0.6 }}>
                                    <Box sx={{ display: 'flex', justify: 'space-between', fontSize: '0.9rem', fontWeight: 900 }}>
                                        <span style={{ flexGrow: 1 }}>{item.name}</span>
                                        <span style={{ width: 60, textAlign: 'right', fontSize: '1.05rem' }}>x {item.quantity}</span>
                                    </Box>
                                    {item.remarks && (
                                        <Box sx={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 'bold', fontStyle: 'italic', pl: 1, mt: 0.25 }}>
                                            * Instruction: {item.remarks}
                                        </Box>
                                    )}
                                    <Box sx={{ borderBottom: '1px dashed #cbd5e1', mt: 0.6 }} />
                                </Box>
                            ))}
                            
                            <Box textAlign="center" sx={{ mt: 3 }}>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#64748b', fontSize: '0.65rem' }}>
                                    Dinexa Smart POS Kitchen Hub
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                        <Button 
                            variant="contained" 
                            color="error" 
                            fullWidth 
                            startIcon={<LocalPrintshopIcon />}
                            onClick={() => { printKOTHTML(kotDetails, currentLanguage); setOpenKOTPreview(false); }}
                            sx={{ py: 1.2, fontWeight: 800, textTransform: 'none', borderRadius: 2, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                        >
                            System KOT
                        </Button>
                        <Button 
                            variant="contained" 
                            color="primary" 
                            fullWidth 
                            startIcon={<LocalPrintshopIcon />}
                            onClick={handleBtKOTPrint}
                            sx={{ py: 1.2, fontWeight: 800, textTransform: 'none', borderRadius: 2, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
                        >
                            {btDeviceName ? `BT: ${btDeviceName}` : 'Bluetooth KOT'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
